/**
 * Company: SPHMedia
 * Description: SectionContainer a container wrapper for sections of a list of articles in landing pages
 */

import Divider from "@assets/divider.svg?react";
import ArrowRight from "@assets/icons/arrow-right.svg?react";
import Link from "@components/Link";
import clsx from "clsx";
import type { ReactElement } from "react";

const Header = ({
  title,
  theme,
  more,
  icon,
  newTab,
}: {
  title: string;
  theme: string;
  more?: string;
  icon?: ReactElement;
  newTab?: boolean;
}) => (
  <>
    <h1
      className={clsx(
        "mx-[12px] flex items-center gap-[8px] pb-[8px] pt-[16px] text-header-2",
        theme === "dark" ? "text-primary-on-dark" : "text-primary",
      )}
    >
      {icon ? <span>{icon}</span> : null}
      {more ? (
        <Link
          to={more}
          {...(newTab && { target: "_blank" })}
          className={clsx("group inline-flex items-center gap-[8px]")}
        >
          {title}
          <div
            className={clsx(
              "flex size-[24px] items-center justify-center rounded-full border-[1.2px] border-solid border-[--color-grey-700] p-px",
              theme === "dark"
                ? "border-white text-white lg:group-hover:bg-white lg:group-hover:text-primary"
                : "text-[--color-grey-700] lg:group-hover:bg-black lg:group-hover:text-primary-on-dark",
            )}
          >
            <ArrowRight />
          </div>
        </Link>
      ) : (
        <span className="inline-flex items-center gap-[8px]">{title}</span>
      )}
    </h1>
    <Divider className="w-full px-[12px]" />
  </>
);

export default function SectionContainer({
  title = "",
  theme = "light",
  children,
  more,
  bgImg,
  testId = "",
  className,
  icon,
  newTab,
  showHeader = true,
}: {
  title?: string;
  theme?: "light" | "dark";
  children: ReactElement | ReactElement[];
  more?: string;
  bgImg?: string;
  testId?: number | string;
  className?: string;
  icon?: ReactElement;
  newTab?: boolean;
  showHeader?: boolean;
}) {
  return (
    <div
      data-testid={`section-container-${testId}`}
      className={clsx(
        theme === "light" ? "bg-secondary" : "bg-inverse",
        className,
      )}
      style={
        bgImg
          ? {
              background: `linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%), url(${bgImg}) lightgray 50% / cover no-repeat`,
            }
          : {}
      }
    >
      <div className="mx-auto max-w-[1200px]">
        {showHeader ? (
          <Header
            title={title}
            theme={theme}
            more={more}
            icon={icon}
            newTab={newTab}
          />
        ) : null}
        {children}
      </div>
    </div>
  );
}
